import { supabase } from '../config/supabase';

export interface SocialLinks {
  user_id: string;
  facebook_url?: string | null;
  instagram_url?: string | null;
  youtube_url?: string | null;
  linkedin_url?: string | null;
  tiktok_url?: string | null;
  show_socials?: boolean | null;
  show_facebook?: boolean | null;
  show_instagram?: boolean | null;
  show_youtube?: boolean | null;
  show_linkedin?: boolean | null;
  show_tiktok?: boolean | null;
  created_at?: string;
  updated_at?: string;
}

const buildEmptyLinks = (userId: string): SocialLinks => ({
  user_id: userId,
  facebook_url: '',
  instagram_url: '',
  youtube_url: '',
  linkedin_url: '',
  tiktok_url: '',
  show_socials: true,
  show_facebook: true,
  show_instagram: true,
  show_youtube: true,
  show_linkedin: true,
  show_tiktok: true,
});

// Fetch social links for a specific user_id (used by public detail views).
export const getSocialLinksByUserId = async (userId: string): Promise<SocialLinks> => {
  if (!userId) {
    throw new Error('userId is required to fetch social links');
  }

  const { data, error } = await supabase
    .from('user_social_links')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching social links by user_id:', error);
    return buildEmptyLinks(userId);
  }

  if (!data) {
    return buildEmptyLinks(userId);
  }

  return data as SocialLinks;
};

// Fetch social links for the currently authenticated user (used by dashboards).
export const getSocialLinksForCurrentUser = async (): Promise<SocialLinks | null> => {
  const { data: authData, error: authError } = await supabase.auth.getUser();

  if (authError) {
    console.error('Error fetching current user for social links:', authError);
    return null;
  }

  const user = authData?.user;
  if (!user) {
    console.warn('No authenticated user when fetching social links');
    return null;
  }

  return getSocialLinksByUserId(user.id);
};

// Update (or create) social links for the currently authenticated user.
export const updateSocialLinksForCurrentUser = async (
  updates: Partial<SocialLinks>
): Promise<SocialLinks | null> => {
  const { data: authData, error: authError } = await supabase.auth.getUser();

  if (authError) {
    console.error('Error fetching current user for updating social links:', authError);
    throw authError;
  }

  const user = authData?.user;
  if (!user) {
    throw new Error('No authenticated user available to update social links');
  }

  const userId = user.id;

  const updateData: Partial<SocialLinks> = {};
  const keys: (keyof SocialLinks)[] = [
    'facebook_url',
    'instagram_url',
    'youtube_url',
    'linkedin_url',
    'tiktok_url',
    'show_socials',
    'show_facebook',
    'show_instagram',
    'show_youtube',
    'show_linkedin',
    'show_tiktok',
  ];

  keys.forEach((key) => {
    if (updates[key] !== undefined) {
      (updateData as any)[key] = updates[key];
    }
  });

  // Always touch updated_at for traceability.
  (updateData as any).updated_at = new Date().toISOString();

  // Check if a record already exists for this user.
  const { data: existing, error: existingError } = await supabase
    .from('user_social_links')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (existingError && existingError.code !== 'PGRST116') {
    // PGRST116 = no rows; treat others as real errors.
    console.error('Error checking existing social links:', existingError);
    throw existingError;
  }

  if (!existing) {
    // Insert new row for this user.
    const insertPayload: Partial<SocialLinks> = {
      ...buildEmptyLinks(userId),
      ...updateData,
      user_id: userId,
    };

    const { data, error } = await supabase
      .from('user_social_links')
      .insert(insertPayload)
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error inserting social links:', error);
      throw error;
    }

    return (data as SocialLinks) ?? null;
  }

  // Update existing row.
  const { data, error } = await supabase
    .from('user_social_links')
    .update(updateData)
    .eq('user_id', userId)
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Error updating social links:', error);
    throw error;
  }

  return (data as SocialLinks) ?? null;
};
